import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import {
  TimeFormats,
  DateFormats,
  NumberSeparators,
  type TimeFormatType,
  type DateFormatType,
  type DecimalsSeparatorType,
  type DigitsSeparatorType,
  type NumberSettingsType,
  type CurrencySettingsType
} from '@models/entities/settings'

import { type UpdateUserSettingsPayloadDataType } from '@models/requests/payloads'

export const usePersonalSettingsStore = defineStore('usePersonalSettingsStore', () => {
  const isChangedSettings = ref(false)
  const isChangedTimezone = ref(false)
  const isShownTimeZoneSettings = ref(false)
  const isShownDateAndTimeSettings = ref(false)
  const isShownNumberSettings = ref(false)
  const isShownCurrencySettings = ref(false)

  const timeZone = ref('Etc/UTC')

  const _timeFormat = ref<TimeFormatType>(TimeFormats.TWENTY_FOUR_HOURS)
  const dateFormat = ref<DateFormatType>(DateFormats.EU_DATE_FORMAT)
  const _numberSettings = ref<NumberSettingsType>({
    decimals_separator: NumberSeparators.COMMA,
    digits_separator: NumberSeparators.SPACE
  })
  const _currencySettings = ref<CurrencySettingsType>({
    sign: false,
    left_position: false
  })

  const isTwelveHoursTimeFormat = computed(() => _timeFormat.value === TimeFormats.TWELVE_HOURS)
  const isEUDateFormat = computed(() => dateFormat.value === DateFormats.EU_DATE_FORMAT)
  const getNumberSettingsDecimalsSeparator = computed<DecimalsSeparatorType>(() => _numberSettings.value.decimals_separator)
  const getNumberSettingsDigitsSeparator = computed<DigitsSeparatorType>(() => _numberSettings.value.digits_separator)
  const getNumberSettingsDecimalsSeparatorSign = computed(() =>
    getNumberSettingsDecimalsSeparator.value === NumberSeparators.DOT ? '.' : ',')
  const getNumberSettingsDigitsSeparatorSign = computed(() => getNumberSettingsDigitsSeparator.value === NumberSeparators.SPACE
    ? ' '
    : getNumberSettingsDigitsSeparator.value === NumberSeparators.DOT ? '.' : ',')
  const getCurrencySettingsIsSign = computed(() => _currencySettings.value.sign)
  const getCurrencySettingsIsLeft = computed(() => _currencySettings.value.left_position)

  const setIsChangedSettings = (status: boolean = true) => {
    isChangedSettings.value = status
  }

  const setIsChangedTimezone = (status: boolean = true) => {
    isChangedTimezone.value = status
  }

  const setShownTimeZoneSettings = (status: boolean = true) => {
    isShownTimeZoneSettings.value = status
  }

  const setShownDateAndTimeSettings = (status: boolean = true) => {
    isShownDateAndTimeSettings.value = status
  }

  const setShownNumberSettings = (status: boolean = true) => {
    isShownNumberSettings.value = status
  }

  const setShownCurrencySettings = (status: boolean = true) => {
    isShownCurrencySettings.value = status
  }

  const setTimeZone = (zone: string, isInit: boolean = false) => {
    timeZone.value = zone

    if (!isInit && !isChangedSettings.value) {
      setIsChangedSettings()
      setIsChangedTimezone()
    }
  }

  const setTimeFormat = (format: TimeFormatType, isInit: boolean = false) => {
    _timeFormat.value = format

    if (!isInit && !isChangedSettings.value) {
      setIsChangedSettings()
    }
  }

  const setDateFormat = (format: DateFormatType, isInit: boolean = false) => {
    dateFormat.value = format

    if (!isInit && !isChangedSettings.value) {
      setIsChangedSettings()
    }
  }

  const setDecimalsSeparatorNumberSettings = (separator: DecimalsSeparatorType, isInit: boolean = false) => {
    _numberSettings.value.decimals_separator = separator

    if (!isInit && !isChangedSettings.value) {
      setIsChangedSettings()
    }
  }

  const setDigitsSeparatorNumberSettings = (separator: DigitsSeparatorType, isInit: boolean = false) => {
    _numberSettings.value.digits_separator = separator

    if (!isInit && !isChangedSettings.value) {
      setIsChangedSettings()
    }
  }

  const setIsSignCurrencySettings = (status: boolean, isInit: boolean = false) => {
    _currencySettings.value.sign = status

    if (!status && _currencySettings.value.left_position) {
      setIsLeftCurrencySettings(false)
    }

    if (!isInit && !isChangedSettings.value) {
      setIsChangedSettings()
    }
  }

  const setIsLeftCurrencySettings = (status: boolean, isInit: boolean = false) => {
    _currencySettings.value.left_position = status

    if (!isInit && !isChangedSettings.value) {
      setIsChangedSettings()
    }
  }

  const prepareSettingsPayloadData = (): UpdateUserSettingsPayloadDataType => {
    return {
      time_format: _timeFormat.value,
      date_format: dateFormat.value,
      number_format: {
        decimals_separator: getNumberSettingsDecimalsSeparator.value,
        digits_separator: getNumberSettingsDigitsSeparator.value
      },
      currency_format: {
        sign: getCurrencySettingsIsSign.value,
        left_position: getCurrencySettingsIsLeft.value
      },
      timezone: timeZone.value
    }
  }

  return {
    isChangedSettings,
    isChangedTimezone,
    isShownTimeZoneSettings,
    isShownDateAndTimeSettings,
    isShownNumberSettings,
    isShownCurrencySettings,
    timeZone,
    isTwelveHoursTimeFormat,
    dateFormat,
    isEUDateFormat,
    getNumberSettingsDecimalsSeparator,
    getNumberSettingsDigitsSeparator,
    getNumberSettingsDecimalsSeparatorSign,
    getNumberSettingsDigitsSeparatorSign,
    getCurrencySettingsIsSign,
    getCurrencySettingsIsLeft,
    setIsChangedSettings,
    setIsChangedTimezone,
    setShownTimeZoneSettings,
    setShownDateAndTimeSettings,
    setShownNumberSettings,
    setShownCurrencySettings,
    setTimeZone,
    setTimeFormat,
    setDateFormat,
    setDecimalsSeparatorNumberSettings,
    setDigitsSeparatorNumberSettings,
    setIsSignCurrencySettings,
    setIsLeftCurrencySettings,
    prepareSettingsPayloadData
  }
})
